import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import listingService from '@/shared/services/listingService';
import usePageTitle from '@/shared/hooks/usePageTitle';
import ListingCard from './components/ListingCard';
import ListingFilters from './components/ListingFilters';

const EMPTY_FILTERS = { institution: '', category: '', building: '', q: '' };

// The route caps a page at 100 and pages with limit and offset, so a page is
// a read rather than a slice of a list the browser already holds. 24 is the
// route's own default and divides the three-column grid evenly.
const PAGE_SIZE = 24;

const listingCount = (n) => `${n} ${n === 1 ? 'listing' : 'listings'}`;

const NO_MATCH = 'No listings match. Try clearing a filter or post an item.';

// An empty select or search box means "no filter", and the server rejects an
// empty string for every one of them, so blanks are dropped rather than
// sent.
function toParams(filters, offset) {
    const params = { limit: PAGE_SIZE, offset };
    for (const [key, value] of Object.entries(filters)) {
        if (value) {
            params[key] = value;
        }
    }
    return params;
}

function BrowsePage() {
    usePageTitle('Browse listings');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    // Applied separately from the form state so typing in the search box does
    // not fire a request per keystroke; only a submit moves this.
    const [applied, setApplied] = useState(EMPTY_FILTERS);
    const [offset, setOffset] = useState(0);
    const [listings, setListings] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async (active, from) => {
        setLoading(true);
        setError('');
        try {
            const data = await listingService.list(toParams(active, from));
            setListings(data.listings || []);
            setTotal(data.total || 0);
        } catch (_error) {
            setListings([]);
            setTotal(0);
            setError(
                'Could not load listings. Check your connection and try again.'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(applied, offset);
    }, [applied, offset, load]);

    // A new filter starts at the first page, or a search run from page two
    // reads as empty when it has matches. The copy is what makes Search a
    // refresh as well: stock moves while a member reads, and passing the same
    // object through would not re-read.
    const handleSearch = () => {
        setApplied({ ...filters });
        setOffset(0);
    };

    // Empty while a read is in flight and while an error stands, because the
    // spinner and the alert each say that themselves and a count from the
    // previous read would be a claim about results nobody is looking at.
    let resultSentence = '';
    if (!loading && !error) {
        resultSentence =
            listings.length === 0 && offset === 0
                ? NO_MATCH
                : listingCount(total);
    }

    return (
        <Container className="py-4">
            <Row className="align-items-center mb-3">
                <Col>
                    <h1 className="h3 mb-0">Browse listings</h1>
                </Col>
                <Col xs="auto">
                    <Button as={Link} to="/listings/new">
                        Post an item
                    </Button>
                </Col>
            </Row>
            <ListingFilters
                value={filters}
                onChange={setFilters}
                onSubmit={handleSearch}
            />
            {/* The result of a search, announced. Mounted before the first
                read runs and left mounted through it, because a live region
                added to the page at the same moment as its text is not
                reliably announced, and a member filtering from the keyboard
                is otherwise told nothing at all: the grid changes silently
                and the count sat in a bare span inside the heading. */}
            <p
                role="status"
                aria-label="Listing results"
                className="text-muted"
            >
                {resultSentence}
            </p>
            {error && <Alert variant="danger">{error}</Alert>}
            {loading && <LoadingSpinner message="Loading listings" />}
            <Row xs={1} md={2} lg={3} className="g-3">
                {listings.map((listing) => (
                    <Col key={listing._id}>
                        <ListingCard listing={listing} />
                    </Col>
                ))}
            </Row>
            {/* The pager outlives an empty page, because a result set that
                shrank between reads leaves the member past the end and
                changing a filter resets the offset, so dropping it here is a
                dead end. */}
            {!loading && !error && (listings.length > 0 || offset > 0) && (
                <nav
                    aria-label="Listing pages"
                    className="d-flex align-items-center gap-3 mt-4"
                >
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={offset === 0}
                        onClick={() =>
                            setOffset(Math.max(0, offset - PAGE_SIZE))
                        }
                    >
                        Previous page
                    </Button>
                    <span className="text-muted small">
                        {listings.length > 0
                            ? `Showing ${offset + 1} to ` +
                              `${offset + listings.length} of ` +
                              listingCount(total)
                            : 'Nothing on this page. ' +
                              `${listingCount(total)} in all.`}
                    </span>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={
                            listings.length === 0 ||
                            offset + listings.length >= total
                        }
                        onClick={() => setOffset(offset + PAGE_SIZE)}
                    >
                        Next page
                    </Button>
                </nav>
            )}
        </Container>
    );
}

export default BrowsePage;
